<template>
  <div>
    <div class="folder-list-container">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="search-container flex-grow-1 mr-3">
          <div class="search-input-wrapper">
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Search in this folder..."
              @keyup.enter="performSearch"
            >
            <svg
              width="13"
              height="13"
              class="search-icon"
            >
              <use href="/img/icons/sprite.svg#search-big" />
            </svg>
            <button 
              v-if="searchQuery" 
              class="clear-search-btn"
              @click="clearSearch"
            >
              <i class="cil-x" />
            </button>
          </div>
        </div>
      </div>

      <BreadcrumbItem
        :breadcrumbs="breadcrumbs"
        @goTo="goToFolder"
      />

      <div v-if="items.length || isListLoading">
        <template v-if="isListLoading">
          <div class="folders-grid">
            <div
              v-for="n in pageSize"
              :key="n"
              class="w-100 h-100"
              style="min-height: 167px;"
            >
              <b-skeleton
                width="100%"
                height="88%"
              />
              <b-skeleton />
            </div>
          </div>
        </template>

        <template v-else>
          <b-alert
            v-if="isSearchActive && items.length && wasEnterPressed"
            show
            variant="info"
            class="mb-3"
          >
            Found {{ totalCount }} result{{ totalCount > 1 ? 's' : '' }} for "{{ searchQuery }}"
          </b-alert>
        
          <b-alert
            v-if="isSearchActive && !items.length && !isListLoading"
            show
            variant="warning"
            class="mb-3"
          >
            No results found for "{{ searchQuery }}"
          </b-alert>

          <div class="section-container">
            <div 
              v-if="folderItems.length" 
              class="folder-items folders-grid"
            >
              <GridItem
                v-for="item in folderItems"
                :key="item.id"
                :item="item"
                :is-selected="isSelected(item)"
                @click="onSelectedItem(item)"
                @dblclick="dblHandler(item)"
              />
            </div>
            <b-alert
              v-else-if="!isSearchActive"
              show
              variant="info"
              class="mb-4"
            >
              No folders available
            </b-alert>
          </div>

          <div class="section-container">
            <div 
              v-if="otherItems.length" 
              class="folders-grid"
            >
              <GridItem
                v-for="otherItem in otherItems"
                :key="otherItem.id"
                :item="otherItem"
                :is-selected="isSelected(otherItem)"
                :is-available-overlay="availableOverlay"
                @click="onSelectedItem(otherItem)"
                @dblclick="fileDoubleClickHandler(otherItem)"
              />
            </div>
          </div>
        </template>
      </div>

      <b-alert
        v-else
        show
        variant="warning"
      >
        Folder is empty
      </b-alert>
    
      <div
        v-if="selectedItems.length"
        class="selection-summary"
      >
        <span class="font-weight-bold">{{ selectedItems.length }} item{{ selectedItems.length > 1 ? 's' : '' }} selected</span>
      </div>
      <div class="d-flex justify-content-center align-items-center">
        <b-pagination
          v-model="currentPage"
          :per-page="pageSize"
          :total-rows="totalCount"
          :disabled="isListLoading"
          @change="onChangePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import GridItem from './browser/GridItem.vue';
import { TABS, MODALS, VIDEO_FILE_TYPES, AUDIO_FILE_TYPES, IMAGE_FILE_TYPES, PDF_FILE_TYPE, PSD_FILE_TYPE } from "./constants";
import { FILE_TYPE, TYPE } from "../../../constants/file-image-types";
import fetchListDTO, {fetchListDataBasedActionCreator} from "./dto/fetch-list-dto";
import BreadcrumbItem from "../../common/atoms/BreadcrumbItem.vue";
import {IMAGE_TYPE_PRODUCT} from "@frontend/constants/file-upload-images-types";
import {createModal} from "./modals/action-creater";

const {
  mapGetters: mapGettersFileBrowser,
  mapActions: mapActionsFileBrowser,
  mapMutations: mapMutationsFileBrowser,
} = createNamespacedHelpers('fileBrowser');

const {
  mapGetters: mapGettersFileUploadImages
} = createNamespacedHelpers('fileUploadImages');

export default {
  components: {BreadcrumbItem, GridItem },
  props: {
    isOnlyFolder: {
      type: Boolean,
      default: false,
    },
    pageSize: {
      type: Number,
      default: 19,
    },
    allowMultipleSelection: {
      type: Boolean,
      default: true,
    }
    
  },
  data() {
    return {
      currentPage: 1,
      selectedItems: [],
      breadcrumbs: [],
      currentFolderId: null,
      searchQuery: '',
      wasEnterPressed: false,
      newFolderName: '',
      isLoading: false,
    }
  },
  computed: {
    ...mapGettersFileBrowser([
      'items',
      'totalCount',
      'isListLoading',
    ]),
    ...mapGettersFileUploadImages([
      'getSelectedImageType',
      'getContentType',
      'getTab',
    ]),
    isSelected() {
      const selectedIds = this.selectedItems.map(({ id }) => id);
      return item => selectedIds.includes(item.id);
    },
    availableOverlay() {
      return [TABS.PRODUCTS, TABS.STOCK_IMAGES, TABS.FILES].includes(this.$route.params.tab);
    },
    folderItems() {
      return this.items.filter(item => item.type === FILE_TYPE.FOLDER);
    },
    otherItems() {
      return this.items.filter(item => item.type !== FILE_TYPE.FOLDER);
    },
    isSearchActive() {
      return !!this.searchQuery.trim();
    }
  },
  mounted() {
    this.setPageSize(this.pageSize);
    this.fetchFolderList();
  },
  methods: {
    ...mapActionsFileBrowser(['fetchList', 'setShowOnlyFolders', 'editFile', 'clearSelection', 'addFolder']),
    ...mapMutationsFileBrowser({
      setPageSize: 'SET_PAGE_SIZE',
      SET_MODAL: 'SET_MODAL'
    }),
    fetchFolderList() {
      this.fetchList(fetchListDataBasedActionCreator(
        this.getTab,
        this.currentFolderId ? this.currentFolderId.toString() : null,
        this.currentPage,
        '',
      ));
    },
    performSearch() {
      if (this.searchQuery.trim()) {
        this.currentPage = 1;
        this.wasEnterPressed = true;
        this.fetchSearchResults();
      } else {
        this.clearSearch();
      }
    },
    clearSearch() {
        this.searchQuery = '';
        this.wasEnterPressed = false;
        this.fetchFolderList();
    },
    fetchSearchResults() {
      this.fetchList(fetchListDataBasedActionCreator(
        this.getTab,
        this.currentFolderId ? this.currentFolderId.toString() : null,
        this.currentPage,
        this.searchQuery,
      ));
    },
    async dblHandler(item) {
      const currentSearchQuery = this.searchQuery;
      this.wasEnterPressed = false;
      this.currentPage = 1;
      this.setSelectedItems();
      this.currentFolderId = item.id;

      if (item.is_root === true) {
        this.breadcrumbs = [];
      }

      if (item.is_root === false) {
        this.breadcrumbs.pop();
      }

      if (item.created_at) {
        this.breadcrumbs.push({
          id: item.id,
          text: item.name,
        });
      }

      this.searchQuery = currentSearchQuery;
      
      if (this.searchQuery.trim()) {
        await this.fetchSearchResults();
      } else {
        await this.fetchFolderList();
      }
    },
    fileDoubleClickHandler(item) {
      if (item.type === FILE_TYPE.FOLDER) return;
      
      if (![VIDEO_FILE_TYPES, AUDIO_FILE_TYPES, IMAGE_FILE_TYPES, PDF_FILE_TYPE, PSD_FILE_TYPE].some(types => types.includes(item.type))) {
        return;
      }
      
      return this.SET_MODAL(createModal(MODALS.PREVIEW_IMAGE, item));
    },
    async goToFolder(folderId, breadcrumbIndex) {
      const currentSearchQuery = this.searchQuery;
      this.wasEnterPressed = false;
      breadcrumbIndex = folderId?.index;
      folderId = folderId?.id;
      if (this.currentFolderId === folderId) return;

      this.currentPage = 1;
      this.setSelectedItems();
      this.currentFolderId = folderId;
      this.breadcrumbs.splice(breadcrumbIndex);

      this.searchQuery = currentSearchQuery;
      
      if (this.searchQuery.trim()) {
        await this.fetchSearchResults();
      } else {
        await this.fetchFolderList();
      }
    },
    onChangePage(page) {
      this.currentPage = page;
      if (this.isSearchActive) {
        this.fetchSearchResults();
      } else {
        this.fetchFolderList();
      }
    },
    setSelectedItems(selectedItems = []) {
      this.selectedItems = selectedItems;
      this.$emit('onSelectedItems', selectedItems);
    },
    onSelectedItem(item) {
      if (!item.created_at) return;

      const isSelected = this.isSelected(item);

      if (isSelected) {
        this.selectedItems = this.selectedItems.filter(selected => selected.id !== item.id);
      } else {
        if (!this.allowMultipleSelection) {
          this.selectedItems = [];
        }
        this.selectedItems.push(item);
      }

      this.setSelectedItems(this.selectedItems);
    },
  }
}
</script>

<style lang="scss">
.folder-list-container {
  .breadcrumb {
    background: unset;
    border-radius: unset;
    border: unset;
  }

  .section-container {
    margin-bottom: 2rem;
  }

  .section-title {
    margin-bottom: 1rem;
    font-weight: 600;
    border-bottom: 1px solid #e9e9e9;
    padding-bottom: 0.5rem;
  }

  .folders-grid {
    display: grid;
    grid-template-rows: auto;
    justify-items: center;
    grid-row-gap: 1rem;
    grid-column-gap: 1rem;
    margin-bottom: 1rem;
    grid-template-columns: repeat(4, 1fr) !important;
  }
  
  .search-container {
    margin-bottom: 0;
  }
  
  .search-input-wrapper {
    position: relative;
    width: 100%;
  }
  
  .search-input {
    width: 100%;
    height: 35px;
    background: white;
    border: 1px solid #b1b1b1;
    border-radius: 12px;
    padding: 0 35px 0 10px;
    outline: 0;
    font-family: "Nunito", sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    color: #464646;
  }
  
  .search-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
  
  .clear-search-btn {
    position: absolute;
    right: 35px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #727272;
    padding: 0;
    font-size: 14px;
    
    &:hover {
      color: #333;
    }
  }
  
  .selection-summary {
    font-size: 14px;
    color: #464646;
  }
}
</style>
